import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  FileText, Calendar, Clock, User, CheckCircle, AlertCircle,
  Loader2, PenTool, ArrowLeft, Share2, MessageSquare, Mail, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WaiverSigningPage() {
  const { referenceNumber, waiverToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [waiverInfo, setWaiverInfo] = useState(null);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    loadWaiverInfo();
  }, [referenceNumber, waiverToken]);

  const loadWaiverInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/waiver/${referenceNumber}/${waiverToken}`);
      if (!response.ok) {
        throw new Error('Waiver not found');
      }
      const data = await response.json();
      setWaiverInfo(data);
      setFullName(data.guest_name || '');
      if (data.waiver_signed) {
        setSigned(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getSignatureData = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!agreed) {
      toast.error('Please read and agree to the waiver terms');
      return;
    }
    if (!hasSignature) {
      toast.error('Please provide your signature');
      return;
    }
    if (!fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    setSigning(true);
    try {
      const response = await fetch(`${API_URL}/api/waiver/${referenceNumber}/${waiverToken}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data: getSignatureData(),
          full_name: fullName,
          date_of_birth: dateOfBirth
        })
      });

      if (!response.ok) {
        throw new Error('Failed to sign waiver');
      }

      const result = await response.json();
      setSigned(true);
      toast.success('Waiver signed successfully!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSigning(false);
    }
  };

  // Share waiver link
  const shareWaiver = async () => {
    const waiverUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Waiver - ${waiverInfo?.tour_title}`,
          text: `Please sign your waiver for our upcoming tour: ${waiverInfo?.tour_title}`,
          url: waiverUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(waiverUrl);
      toast.success('Waiver link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading waiver...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Waiver Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return Home
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Waiver Signed!</h1>
          <p className="text-muted-foreground mb-4">
            Thank you, {fullName}! Your waiver for <strong>{waiverInfo?.tour_title}</strong> has been successfully signed.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {waiverInfo?.tour_date}
              <Clock className="h-4 w-4 ml-2" />
              {waiverInfo?.tour_time}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Reference: <strong>{referenceNumber}</strong>
            </p>
          </div>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return Home
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold">{waiverInfo?.waiver_template?.title || 'Liability Waiver'}</h1>
          <p className="text-muted-foreground mt-2">
            Please read and sign the waiver below
          </p>
        </div>

        {/* Booking Info */}
        <Card className="p-4 mb-6 bg-primary/5 border-primary/20">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-lg">{waiverInfo?.tour_title}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {waiverInfo?.tour_date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {waiverInfo?.tour_time}
                </span>
              </div>
            </div>
            <Badge variant="outline" className="text-sm">
              Ref: {referenceNumber}
            </Badge>
          </div>
        </Card>

        {/* Guest Name */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Guest Information</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="fullName">Full Legal Name *</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </Card>

        {/* Waiver Content */}
        <Card className="p-6 mb-6">
          <div 
            className="prose prose-sm max-w-none overflow-y-auto max-h-[400px] pr-4"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(waiverInfo?.waiver_template?.content || '') }}
          />
        </Card>

        {/* Agreement Checkbox */}
        <Card className="p-4 mb-6">
          <div className="flex items-start gap-3">
            <Checkbox 
              id="agree" 
              checked={agreed} 
              onCheckedChange={setAgreed}
              className="mt-1"
            />
            <Label htmlFor="agree" className="font-normal cursor-pointer">
              I have read and understand the waiver above. I voluntarily agree to its terms and conditions, 
              and I confirm that I am signing this waiver freely and without coercion.
            </Label>
          </div>
        </Card>

        {/* Signature Pad */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Your Signature</h2>
            </div>
            <Button variant="outline" size="sm" onClick={clearSignature}>
              Clear
            </Button>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full h-[150px] cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Sign above using your mouse or finger
          </p>
        </Card>

        {/* Submit Button */}
        <div className="flex flex-col gap-4">
          <Button 
            onClick={handleSubmit} 
            disabled={signing || !agreed || !hasSignature || !fullName.trim()}
            className="w-full py-6 text-lg"
          >
            {signing ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Sign Waiver
              </>
            )}
          </Button>

          <Button variant="outline" onClick={shareWaiver} className="w-full">
            <Share2 className="h-4 w-4 mr-2" />
            Share This Waiver Link
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          By signing, you agree to the terms and conditions of Tour Finder Puerto Rico.
          <br />
          If you have questions, please contact us before signing.
        </p>
      </div>
    </div>
  );
}
